import { useState, useEffect } from 'react';

import { Input, Select, Button, useDisclosure, useToast } from '@chakra-ui/react';
import { HttpStatusCode } from 'axios';
import { useNavigate } from 'react-router-dom';
import { ALLOWED_CHARACTERS_REGEX, DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE, DURATION_TOAST, COLORS, ROLES } from '@/common/constants';
import ModalDelete from '@/components/Modals/ModalDelete';
import TableInvoiceInspectorSuperAdmin from '@/components/Tables/TableInvoiceInspectorSuperAdmin';
import TableInvoiceInspector from '@/components/Tables/TableInvoiceInspector';
import ModalInvoiceConfirmation from '@/components/Modals/ModalInvoiceConfirmation';
import ModalConfirmToPay from '@/components/Modals/ModalConfirmToPay';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { RootState } from '@/redux/store';
import { setDataMultiplePayment } from "@/redux/slices/paymentSlice";
import { setDataTotalNewInvoiceInspectingCompany, setDataTotalNewInvoiceShipOperator } from '@/redux/slices/notificationSlice';
import { downloadFile, downloadFileZip } from '@/services/downloadService';
import { deleteInvoiceInspector, getListInvoiceInspector, getListInvoiceInspectorById, updateConfirmPaymentInspector, updateInvoiceStatusInspector } from '@/services/invoicesInspectorService';
import { getNewRecordCounterAdminFinance, getNewRecordCounterInspectingCompany, getNewRecordCounterSuperAdmin } from '@/services/newRecordCounterService';
import { FaMoneyBill } from "react-icons/fa";
import '../../styles/ListPage.css';
import { getDataRef } from '@/services/refDataService';

const InvoicesInspectorList = () => {
    const navigate = useNavigate();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const { isOpen: isOpenView, onOpen: onOpenView, onClose: onCloseView } = useDisclosure();
    // const { isOpen: isOpenBulk, onClose: onCloseBulk } = useDisclosure();
    const { isOpen: isOpenConfirmation, onOpen: onOpenConfirmation, onClose: onCloseConfirmation } = useDisclosure();
    const role = useAppSelector((state: RootState) => state.account.roleName);
    // const dataRaw = useAppSelector((state: RootState) => state.payment.multiplePayment);
    const name = useAppSelector((state: RootState) => state.account.name);
    const inspectorId = useAppSelector((state: RootState) => state?.account?.appId);
    const toast = useToast();
    const dispatch = useAppDispatch();

    const [approvalNote, setApprovalNote] = useState('');
    const [approvalStatus, setApprovalStatus] = useState('-1');
    const [currentPageNumber, setCurrentPageNumber] = useState(DEFAULT_PAGE_NUMBER);
    const [currentPageSize, setCurrentPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [dataList, setDataList] = useState([]) as any;
    const [errorMessageApproval, setErrorMessageApproval] = useState('');
    const [errorMessageNote, setErrorMessageNote] = useState('');
    const [hasNextPage, setHasNextPage] = useState(false);
    const [id, setId] = useState('');
    const [idViewConfirmation, setIdViewConfirmation] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [, setIsValidToPay] = useState(false);
    const [invoiceStatus, setInvoiceStatus] = useState('') as any;
    const [paymentStatus, setPaymentStatus] = useState('') as any;
    const [market, setMarket] = useState('') as any;
    const [searchValue, setSearchValue] = useState('');
    const [selectedDataView, setSelectedDataView] = useState(null) as any;
    const [totalPageNumber, setTotalPageNumber] = useState(1);
    const [selectedSequenceId, setSelectedSequenceId] = useState('');
    const [dataConfirmation, setDataConfirmation] = useState(null) as any;
    const [idConfirmationPayment, setIdConfirmationPayment] = useState('');
    const [listMarket, setListMarket] = useState([]) as any;
    // multiple payment
    const multiplePayment = useAppSelector((state: RootState) => state.payment.multiplePayment);

    const handleChangeSearch = (value: string) => {
        setSearchValue(value);
    }

    const handleSubmitSearch = () => {
        getData();
    }

    const handleEdit = (idEdit: string) => {
        if (role === 'super admin') {
            navigate('/manage-invoices/inspecting-company/edit', { state: { id: idEdit } });
        } else {
            navigate('/manage-invoices-inspecting-company/edit', { state: { id: idEdit } });
        }
    }

    const handleDelete = (id: string) => {
        setId(id);
        onOpen();
    }

    const handleConfirmDelete = async () => {
        const obj = {
            invoiceId: id,
            modifiedBy: name || "System"
        }

        const response = await deleteInvoiceInspector(obj) as any;
        if (response.status === HttpStatusCode.Ok) {
            getData();
            toast({
                title: 'Invoice deleted.',
                description: "We've deleted invoice for you.",
                status: 'success',
                duration: DURATION_TOAST,
                isClosable: true,
            });
        } else {
            toast({
                title: 'Invoice failed to delete.',
                description: "We failed to delete invoice for you.",
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
        }

        onClose();
    }

    const handleView = (idView: string, sequenceID: string) => {
        setSelectedSequenceId(sequenceID);
        setApprovalStatus('-1');
        setErrorMessageApproval('');
        setErrorMessageNote('');
        setIdViewConfirmation(idView);
        const data = dataList.find((x: any) => x.id === idView);
        if (data) {
            setSelectedDataView(data);
        }

        onOpenView();
    }

    const handleConfirmToCancelConfirmation = () => {
        setApprovalStatus('');
        onCloseConfirmation();
    }

    const handleConfirmView = async () => {
        if (approvalStatus === '3' && approvalNote.trim() === '') {
            setErrorMessageNote('Note is required');
            return;
        }

        if (approvalStatus === '-1'){
            setErrorMessageApproval('Please choose either approve or reject.');
            return;
        }

        if (errorMessageNote) {
            return;
        }

        const obj = {
            invoiceId: idViewConfirmation,
            sequenceID: selectedSequenceId,
            invoiceStatus: Number(approvalStatus),
            note: approvalNote,
            modifiedBy: name || "System"
        }

        const response = await updateInvoiceStatusInspector(obj) as any;
        if (response.status === HttpStatusCode.Ok) {
            getData();
            toast({
                title: 'Invoice status updated.',
                description: "We've updated invoice status for you.",
                status: 'success',
                duration: DURATION_TOAST,
                isClosable: true,
            });
        } else {
            toast({
                title: 'Invoice failed to update.',
                description: "We failed to update invoice status for you.",
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
        }

        onCloseView();
    }

    const handlePay = (idPay: string) => {
        navigate('/manage-invoices/inspecting-company/payment', { state: { id: idPay, type: 'inspector' } });
    }

    const handleMultiPay = () => {
        navigate('/manage-invoices/inspecting-company/multiple-payment', { state: { type: 'inspector' } });
    }

    const handleChangeApprovalStatus = (value: string) => {
        if (value !== '-1'){
            setErrorMessageApproval('');
        }
        setApprovalStatus(value);
    }

    const handleChangeNote = (value: string) => {
        setErrorMessageNote('');

        if (value) {
            if (value.length > 50) {
                setErrorMessageNote('Max length is 50 characters');
            }

            const result = ALLOWED_CHARACTERS_REGEX.test(value);
            if (!result) {
                setErrorMessageNote('Input must only contain alphabets, numbers, commas, dots, slashes, and dashes.');
            }
        } else {
            setErrorMessageNote('Note is required');
        }

        setApprovalNote(value);
    }

    const handleConfirmToCancel = () => {
        setApprovalStatus('');
        onCloseView();
    }

    const handleChangeInvoiceStatus = (value: number) => {
        setInvoiceStatus(value);
    }

    const handleChangePaymentStatus = (value: number) => {
        setPaymentStatus(value);
    }

    const handleChangeMarketType = (value: string) => {
        setMarket(value);
    }

    const handleDownload = async (idFile: string) => {
        await downloadFile(idFile);
    }

    const handleDownloadZip = async (idFile: string, uploadType: number) => {
        await downloadFileZip(idFile, uploadType);
    }

    const setLatestCounterAdmin = async () => {
        const responseCounter = await getNewRecordCounterSuperAdmin() as any;
        if (responseCounter?.status === HttpStatusCode.Ok) {
            dispatch(setDataTotalNewInvoiceInspectingCompany(responseCounter?.data?.invoiceInspectingCompanyAdmin || 0));
        }
    }

    const setLatestCounterAdminFinance = async () => {
        const responseCounter = await getNewRecordCounterAdminFinance() as any;
        if (responseCounter?.status === HttpStatusCode.Ok) {
            dispatch(setDataTotalNewInvoiceInspectingCompany(responseCounter?.data?.inspectingCompany || 0));
            dispatch(setDataTotalNewInvoiceShipOperator(responseCounter?.data?.shipOperator || 0));
        }
    }

    const setLatestCounterInspectingCompany = async () => {
        const responseCounter = await getNewRecordCounterInspectingCompany(inspectorId) as any;
        if (responseCounter?.status === HttpStatusCode.Ok) {
            dispatch(setDataTotalNewInvoiceInspectingCompany(responseCounter?.data?.invoices || 0));
        }
    }

    const handlePayment = async (idPay: string) => {
        setErrorMessageNote('');
        setIdConfirmationPayment(idPay);
        const data = dataList.find((x: any) => x.id === idPay);
        let newData = data;
        if (data) {
            newData = { ...data, invoiceID: data.invoiceNumber }
        }
        setDataConfirmation(newData);
        onOpenConfirmation();
    }

    const handleConfirmPayment = async () => {
        const data = new FormData();
        data.append('id', idConfirmationPayment);
        data.append('modifiedBy', name || "System");
        // for (let [key, value] of data.entries()) {
        //     console.log(`${key}: ${value}`);
        // }
        const response = await updateConfirmPaymentInspector(data) as any;
        if (response.status === HttpStatusCode.Ok) {
            getData();
            toast({
                title: 'Payment confirmed.',
                description: "We've confirmed the payment.",
                status: 'success',
                duration: DURATION_TOAST,
                isClosable: true,
            });
        } else {
            toast({
                title: 'Invoice failed to update.',
                description: "We failed to confirm the payment.",
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
        }

        onCloseConfirmation();
    }

    const getData = async () => {
        const param = {
            search: searchValue,
            pageNumber: currentPageNumber === 0 ? 1 : currentPageNumber,
            pageSize: currentPageSize,
            status: '',
            invoiceStatus,
            paymentStatus,
            market,
        };

        let response = {} as any;

        if (role === 'super admin' || role === 'admin finance') {
            if ((multiplePayment?.data?.length ?? 0) > 0) {
                if (invoiceStatus === '') {
                    param.invoiceStatus = 1;
                    setInvoiceStatus(1);
                }
                if (paymentStatus === '') {
                    param.paymentStatus = 0;
                    setPaymentStatus(0);
                }
            }
            response = await getListInvoiceInspector(param) as any;

            if (role === 'super admin') {
                setLatestCounterAdmin();
            } else if (role === 'admin finance') {
                setLatestCounterAdminFinance();
            }
        } else if (role === 'inspecting company') {
            const newParam = { ...param, id: inspectorId };
            response = await getListInvoiceInspectorById(newParam) as any;
            setLatestCounterInspectingCompany();
        }

        if (response.status === HttpStatusCode.Ok) {
            setDataList(response.data);
            setTotalPageNumber(response.totalPage);
            setCurrentPageNumber(response.pageNumber);
            setHasNextPage(response.hasNextPage);
        } else {
            toast({
                title: response?.error?.code,
                description: response?.error?.message,
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
        }

        setIsLoading(false);
    }

    const getListRef = async (type: string) => {
        const response = await getDataRef(type) as any;
        if (response.status === HttpStatusCode.Ok) {
            const formattedData = response.data.map((item: any) => ({
                value: item.value,
                label: item.name,
            }));

            if (type === 'market') {
                setListMarket(formattedData);
            }
        } else {
            setListMarket([]);
        }
    }

    useEffect(() => {
        getListRef('market');
        getData();
    }, []);

    useEffect(() => {
        getData();
    }, [currentPageSize, currentPageNumber])

    return (
        <>
            <ModalConfirmToPay isOpen={isOpenConfirmation} onClose={onCloseConfirmation} handleConfirmToCancel={handleConfirmToCancelConfirmation} handleConfirm={handleConfirmPayment} dataConfirmation={dataConfirmation} handleDownload={handleDownload} />
            {/* <ModalBulkPaymentConfirmation isOpen={isOpenBulk} onClose={()=> setOpenBulk(false)} isLoadingBulk={isLoadingBulk} handleConfirmToCancel={() => setOpenBulk(false)} handleConfirm={handleConfirmBulk} bulkPaymentDate={bulkPaymentDate} setBulkPaymentDate={setBulkPaymentDate} handleDrop={handleDrop} uploadedFile={uploadedFile} /> */}
            <ModalInvoiceConfirmation isOpen={isOpenView} onClose={onCloseView} handleChangeNote={handleChangeNote} handleConfirmToCancel={handleConfirmToCancel} handleConfirm={handleConfirmView} approvalStatus={approvalStatus} handleChangeApprovalStatus={handleChangeApprovalStatus} selectedDataView={selectedDataView} errorMessageApproval={errorMessageApproval} errorMessageNote={errorMessageNote} />
            <ModalDelete title="Delete it?" isOpen={isOpen} onClose={onClose} handleConfirmDelete={handleConfirmDelete} />
            <div className="table-container">
                {[ROLES.INSPECTING_COMPANY].includes(role) &&
                    <div className="table-filter-action-container">
                        <div className="table-filter-container">
                            <div className="table-filter-input filter-input-width-8">
                                <Input placeholder='Search' onChange={(e: any) => handleChangeSearch(e.target.value)} />
                            </div>
                            <div className="table-filter-input filter-input-width-5">
                                <Select onChange={(e: any) => handleChangeMarketType(e.target.value)}>
                                    <option key="all" value=''>All Market</option>
                                    {listMarket.map((item: any) => (
                                        <option key={item.value} value={item.value}>{item.label}</option>
                                    ))}
                                </Select>
                            </div>
                            <div className="table-filter-input filter-input-width-8">
                                <Select onChange={(e: any) => handleChangePaymentStatus(e.target.value)}>
                                    <option key="all" value=''>All Payment Status</option>
                                    <option key="unpaid" value={0}>Unpaid</option>
                                    <option key="paid" value={1}>Paid</option>
                                </Select>
                            </div>
                            <div>
                                <Button className='table-search-button' bgColor={COLORS.BUTTON.BACKGROUND} color={COLORS.BUTTON.FOREGROUND} variant='solid' borderRadius={10} onClick={handleSubmitSearch}>
                                    Search
                                </Button>
                            </div>
                        </div>
                    </div>
                }
                {([ROLES.SUPER_ADMIN, ROLES.FINANCE].includes(role)) &&
                    <div className="table-filter-action-container">
                        <div className="table-filter-container">
                            <div className="table-filter-input filter-input-width-7">
                                <Input placeholder='Search' onChange={(e: any) => handleChangeSearch(e.target.value)} />
                            </div>
                            <div className="table-filter-input filter-input-width-8">
                                <Select value={invoiceStatus} onChange={(e: any) => handleChangeInvoiceStatus(e.target.value)}>
                                    <option key="all" value=''>All Invoice Status</option>
                                    <option key="approved" value={1}>Approved</option>
                                    <option key="new" value={2}>New</option>
                                    <option key="reject" value={3}>Rejected</option>
                                </Select>
                            </div>
                            <div className="table-filter-input filter-input-width-5">
                                <Select value={market} onChange={(e: any) => handleChangeMarketType(e.target.value)}>
                                    <option key="all" value=''>All Market</option>
                                    {listMarket.map((item: any) => (
                                        <option key={item.value} value={item.value}>{item.label}</option>
                                    ))}
                                </Select>
                            </div>
                            <div className="table-filter-input filter-input-width-8">
                                <Select value={paymentStatus} onChange={(e: any) => handleChangePaymentStatus(e.target.value)}>
                                    <option key="all" value=''>All Payment Status</option>
                                    <option key="unpaid" value={0}>Unpaid</option>
                                    <option key="paid" value={1}>Paid</option>
                                    <option key="completed" value={4}>Completed</option>
                                </Select>
                            </div>
                            <div>
                                <Button className='table-search-button' bgColor={COLORS.BUTTON.BACKGROUND} color={COLORS.BUTTON.FOREGROUND} variant='solid' borderRadius={10} onClick={handleSubmitSearch}>
                                    Search
                                </Button>
                            </div>
                            {(role === ROLES.FINANCE && multiplePayment?.data && multiplePayment?.data.length > 1) && (
                                <>
                                    <div className="table-filter-separator" />
                                    <Button
                                        leftIcon={<FaMoneyBill />}
                                        rightIcon={
                                            <span style={{
                                                display: 'inline-block',
                                                background: '#fff',
                                                color: '#079455',
                                                borderRadius: '10px',
                                                padding: '2px 8px',
                                                fontWeight: 'bold',
                                                fontSize: '13px',
                                                boxShadow: '0 1px 4px rgba(0,0,0,0.08)'
                                            }}>{multiplePayment?.data.length}</span>
                                        }
                                        bgColor={"#079455"}
                                        color={'white'}
                                        className='table-add-button'
                                        variant='solid'
                                        borderRadius={10}
                                        onClick={() => handleMultiPay()}
                                        style={{ marginRight: 8 }}
                                    >
                                        Multiple Pay
                                    </Button>
                                    <Button
                                        colorScheme="red"
                                        variant="outline"
                                        borderRadius={10}
                                        onClick={() => dispatch(setDataMultiplePayment({ id: '', data: [] }))}
                                    >
                                        Clear
                                    </Button>
                                </>
                            )}
                        </div>
                    </div>
                }
                {/* {(role === 'admin finance') && <Button px={8} bgColor={COLORS.BUTTON.BACKGROUND} color={COLORS.BUTTON.FOREGROUND} variant='solid' borderRadius={10} style={{ marginBottom: 20 }} onClick={handleMultiplePayment} isDisabled={!isValidToPay}>Multiple Payment</Button>} */}
                {(role === 'super admin' || role === 'admin finance') && <TableInvoiceInspectorSuperAdmin dataList={dataList} isLoading={isLoading} setIsValidToPay={setIsValidToPay} handleEdit={handleEdit} handleDelete={handleDelete} currentPageSize={currentPageSize} handleChangePageSize={setCurrentPageSize} handleChangePageNumber={setCurrentPageNumber} currentPageNumber={currentPageNumber} totalPageNumber={totalPageNumber} hasNextPage={hasNextPage} handleView={handleView} handlePay={handlePay} handleDownload={handleDownload} handleDownloadZip={handleDownloadZip} role={role} />}
                {role === 'inspecting company' && <TableInvoiceInspector dataList={dataList} isLoading={isLoading} handleEdit={handleEdit} handleDelete={handleDelete} handlePay={handlePayment} currentPageSize={currentPageSize} handleChangePageSize={setCurrentPageSize} handleChangePageNumber={setCurrentPageNumber} currentPageNumber={currentPageNumber} totalPageNumber={totalPageNumber} hasNextPage={hasNextPage} />}
            </div>
        </>
    )
}

export default InvoicesInspectorList;